import React from 'react';
import EazyLogo from './EazyLogo';

export default function PrivacyPolicy() {
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#0a0a1e', color: 'rgba(255,255,255,0.85)', fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '60px 24px 80px' }}>

        <div style={{ marginBottom: 48 }}>
          <a href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', marginBottom: 40 }}>
            <EazyLogo size="sm" theme="dark" />
          </a>
          <h1 style={{ fontSize: 32, fontWeight: 700, color: 'white', margin: '0 0 8px' }}>Privacy Policy</h1>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', margin: 0 }}>Last updated: May 2026</p>
        </div>

        <Section title="1. Who We Are">
          <p>eazyproject is an AI-powered project management platform operated by eazyproject. If you have any questions about this policy or how we handle your data, you can reach us at <a href="mailto:noreply@eazyproject.cloud" style={{ color: '#818cf8' }}>noreply@eazyproject.cloud</a>.</p>
        </Section>

        <Section title="2. What Data We Collect">
          <p>When you create an account or use our platform, we collect the following information:</p>
          <ul>
            <li><strong>Account data:</strong> your full name, email address, and password (stored as a secure hash).</li>
            <li><strong>Organization data:</strong> your organization's name and your role within it.</li>
            <li><strong>Project data:</strong> any content you create within the platform — projects, tasks, documents, notes, and related files.</li>
            <li><strong>Billing data:</strong> your subscription plan. Payment details (card numbers, billing address) are handled exclusively by Stripe and are never stored on our servers.</li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Data">
          <p>We use your data solely to provide and operate the eazyproject service:</p>
          <ul>
            <li>To create and manage your account and organization.</li>
            <li>To process subscription payments through Stripe.</li>
            <li>To send you transactional emails — account confirmations, payment receipts, and service notifications. We do not send marketing emails without your consent.</li>
            <li>To power AI-assisted features using the content you provide within your projects.</li>
          </ul>
          <p>We do not sell your data, use it for advertising, or share it with third parties for their own purposes.</p>
        </Section>

        <Section title="4. Third-Party Services">
          <p>To operate our platform, we work with the following trusted providers. Each acts as a data processor under a formal agreement:</p>
          <ul>
            <li><strong>Supabase</strong> — database and authentication infrastructure. Your data is stored in Supabase-managed servers within the EU.</li>
            <li><strong>Stripe</strong> — payment processing. Stripe handles all payment card data under their own PCI-compliant infrastructure. <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer" style={{ color: '#818cf8' }}>Stripe Privacy Policy →</a></li>
            <li><strong>Resend</strong> — transactional email delivery. Used exclusively to send system emails triggered by your account activity.</li>
          </ul>
        </Section>

        <Section title="5. Data Retention">
          <p>We retain your data for as long as your account is active. If your subscription is suspended:</p>
          <ul>
            <li>Your account and data remain intact for <strong>5 months</strong>, during which you can reactivate at any time.</li>
            <li>After 5 months of inactivity, your project data is permanently deleted. You will receive an email warning 15 days before this happens.</li>
            <li>Your account credentials (email, name) may be retained for a short period after data deletion for legal and accounting purposes.</li>
          </ul>
        </Section>

        <Section title="6. Your Rights (GDPR)">
          <p>If you are based in the European Union, you have the following rights regarding your personal data:</p>
          <ul>
            <li><strong>Right of access:</strong> you can request a copy of the data we hold about you.</li>
            <li><strong>Right to rectification:</strong> you can correct inaccurate data directly from your profile settings.</li>
            <li><strong>Right to erasure:</strong> you can request that we delete your account and personal data.</li>
            <li><strong>Right to portability:</strong> you can request an export of your data in a machine-readable format.</li>
            <li><strong>Right to object:</strong> you can object to certain types of processing.</li>
          </ul>
          <p>To exercise any of these rights, contact us at <a href="mailto:noreply@eazyproject.cloud" style={{ color: '#818cf8' }}>noreply@eazyproject.cloud</a>. We will respond within 30 days.</p>
        </Section>

        <Section title="7. Security">
          <p>We take reasonable technical and organizational measures to protect your data. Passwords are hashed using bcrypt and never stored in plain text. All data is transmitted over HTTPS. Access to production systems is restricted to authorized personnel only.</p>
        </Section>

        <Section title="8. Changes to This Policy">
          <p>We may update this policy from time to time. If we make significant changes, we will notify you by email or with a notice on the platform. The date at the top of this page always reflects the most recent version.</p>
        </Section>

        <div style={{ marginTop: 56, paddingTop: 32, borderTop: '1px solid rgba(255,255,255,0.08)', textAlign: 'center' }}>
          <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, margin: '0 0 12px' }}>© {new Date().getFullYear()} eazyproject</p>
          <a href="/cookies" style={{ color: '#818cf8', fontSize: 13, textDecoration: 'none' }}>Cookie Policy →</a>
        </div>

      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, color: 'white', margin: '0 0 14px', paddingBottom: 10, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>{title}</h2>
      <div style={{ fontSize: 15, lineHeight: 1.8, color: 'rgba(255,255,255,0.7)' }}>
        {children}
      </div>
    </div>
  );
}
